// The Import files preview map, as data: every read file's lines (all drawn red, dashed when
// left out; a hike from waypoints as the solid straight line it saves through its waypoints),
// the waypoints it keeps as pins, every other file faded while one is open, and the points the
// camera frames. Pure, so ImportPreviewMap.vue only draws it.
// Ported from Android's `ImportMapModel.kt`.

import type { ImportPoint } from './importTypes';
import { fromWaypointsDay, keptWaypoints, plannedLines, recordingTracks, thinned, waypointsToAdd } from './importPayloads';
import { isSingle, willImport, type ImportScreenState } from './importScreenState';
import { appendAll } from './importRules';

/** How a line is drawn: by what its file becomes. */
export type ImportMapLineStyle = 'recording' | 'planned' | 'skipped';

export interface ImportMapLine {
  points: ImportPoint[];
  style: ImportMapLineStyle;
  /** Drawn faded because another file is open. */
  dimmed: boolean;
  /** Drawn dashed: the file is left out. */
  dashed: boolean;
}

export interface ImportMapModel {
  lines: ImportMapLine[];
  pins: ImportPoint[];
  /** The open file's lines and pins on a several-file screen; empty when none is open. */
  focusPoints: ImportPoint[];
}

/** Per line, at most this many points reach the map — plenty for a small preview. */
export const MAP_PREVIEW_MAX_POINTS = 400;

/** At most this many waypoint pins in total; a big waypoint file doesn't need every pin. */
export const MAP_PREVIEW_MAX_PINS = 200;

/** Every drawn point. */
export const allPoints = (m: ImportMapModel): ImportPoint[] => [...m.lines.flatMap(l => l.points), ...m.pins];

/** What the camera frames: the open file, or everything when none is open. */
export const cameraPoints = (m: ImportMapModel): ImportPoint[] => (m.focusPoints.length ? m.focusPoints : allPoints(m));

export const isEmptyMap = (m: ImportMapModel): boolean => !m.lines.length && !m.pins.length;

export function mapModel(s: ImportScreenState): ImportMapModel {
  const lines: ImportMapLine[] = [];
  const pins: ImportPoint[] = [];
  const focused: ImportPoint[] = [];
  const focus = isSingle(s) ? null : s.openId;
  for (const entry of s.entries) {
    const p = entry.preview;
    if (!p || p.error) continue;
    const dimmed = focus !== null && focus !== entry.id;
    const linesBefore = lines.length;
    const pinsBefore = pins.length;
    const importing = willImport(entry);
    const choice = entry.settings.choice;
    const style: ImportMapLineStyle = !importing ? 'skipped' : choice === 'recording' ? 'recording' : 'planned';
    const drawn =
      choice === 'recording'
        ? recordingTracks(p)
        : choice === 'planned'
          ? plannedLines(p, entry.settings)
          : p.lines.filter(l => l.duplicateOf === null);
    drawn.forEach(line => {
      line.segments
        .filter(seg => seg.length >= 2)
        .forEach(seg => lines.push({ points: thinned(seg, MAP_PREVIEW_MAX_POINTS), style, dimmed, dashed: style === 'skipped' }));
    });
    // A hike from waypoints is drawn as it's saved: straight through the waypoints, in file order.
    if (choice === 'fromWaypoints') {
      const through = fromWaypointsDay(p).points;
      if (through.length >= 2) lines.push({ points: thinned(through, MAP_PREVIEW_MAX_POINTS), style, dimmed, dashed: false });
    }
    if (importing && choice !== 'recording') {
      appendAll(pins, waypointsToAdd(p, entry.settings).map(w => w.point));
    } else if (!p.lines.length) {
      appendAll(pins, keptWaypoints(p).map(w => w.point));
    }
    if (entry.id === focus) {
      appendAll(focused, lines.slice(linesBefore).flatMap(l => l.points));
      appendAll(focused, pins.slice(pinsBefore));
      // A file drawn as nothing (no waypoints kept, say) still has its lines to show.
      if (!focused.length) appendAll(focused, p.lines.flatMap(l => l.segments.flat()));
    }
  }
  return {
    lines,
    pins: thinned(pins, MAP_PREVIEW_MAX_PINS),
    focusPoints: thinned(focused, MAP_PREVIEW_MAX_POINTS)
  };
}

/** [[west, south], [east, north]] around `points`, or null when there are none. */
export function boundsOf(points: ImportPoint[]): [[number, number], [number, number]] | null {
  if (!points.length) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const p of points) {
    west = Math.min(west, p.lon);
    east = Math.max(east, p.lon);
    south = Math.min(south, p.lat);
    north = Math.max(north, p.lat);
  }
  return [[west, south], [east, north]];
}
